import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, ArrowUp, ArrowDown, FloppyDisk, ArrowsClockwise, PencilSimple, X } from "@phosphor-icons/react";

// Señales que la IA detecta en cada mensaje entrante (score_inbound).
// El motor de avance las evalúa contra las condiciones de cada transición.
const SIGNALS = [
  { value: "semantic", label: "Instrucción en lenguaje natural (IA decide)", type: "semantic" },
  { value: "engagement_score", label: "Engagement (qué tan enganchado está)", type: "number" },
  { value: "leadership_score", label: "Liderazgo (perfil de líder)", type: "number" },
  { value: "detail_level", label: "Nivel de detalle de su respuesta", type: "number" },
  { value: "is_interested", label: "Muestra interés", type: "bool" },
  { value: "wants_to_join", label: "Quiere unirse / acepta", type: "bool" },
  { value: "asks_how_to_help", label: "Pregunta cómo ayudar", type: "bool" },
  { value: "mentions_others", label: "Menciona a otras personas", type: "bool" },
  { value: "explicitly_rejects", label: "Rechaza explícitamente", type: "bool" },
  { value: "emotion", label: "Emoción del mensaje", type: "enum",
    options: ["positive", "enthusiastic", "neutral", "negative", "frustrated"] },
];
const signalMeta = (name) => SIGNALS.find((s) => s.value === name) || SIGNALS[0];

export default function PipelineDetail() {
  const { pipelineId } = useParams();
  const navigate = useNavigate();
  const { data, loading, reload } = useFetch(() => api.get(`/pipelines/${pipelineId}`).then((r) => r.data), [pipelineId]);
  const { data: agents } = useFetch(() => api.get("/agents").then((r) => r.data), []);

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // Initialize draft when pipeline loads
  useEffect(() => {
    if (data?.pipeline && !draft) {
      setDraft({
        name: data.pipeline.name,
        description: data.pipeline.description || "",
        target_role: data.pipeline.target_role || "",
        target_user_side: data.pipeline.target_user_side || "",
        active: data.pipeline.active,
        stages: (data.pipeline.stages || []).map((s) => ({ ...s })),
        transitions: (data.pipeline.transitions || []).map((t) => ({ ...t, condition: { ...(t.condition || {}) } })),
      });
    }
    // eslint-disable-next-line
  }, [data]);

  if (loading || !draft) return <div className="p-10 text-carbon-500">Cargando...</div>;
  if (!data) return <div className="p-10 text-signal">Pipeline no encontrado</div>;
  const p = data.pipeline;

  // Group members per stage (using saved data, not draft — refleja DB real)
  const byStage = {};
  (data.members || []).forEach((m) => {
    const k = m.current_stage_id || "none";
    (byStage[k] = byStage[k] || []).push(m);
  });

  const agentName = (id) => (agents || []).find((a) => a.id === id)?.name || "—";

  const updateStage = (idx, patch) => {
    const next = [...draft.stages];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, stages: next });
  };

  const moveStage = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= draft.stages.length) return;
    const next = [...draft.stages];
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((s, i) => { s.order = i; });
    setDraft({ ...draft, stages: next });
  };

  const removeStage = (idx) => {
    const s = draft.stages[idx];
    if ((byStage[s.id] || []).length > 0) {
      if (!confirm(`Este stage tiene ${(byStage[s.id] || []).length} usuarios asignados. Si lo borras quedarán sin stage. ¿Continuar?`)) return;
    }
    const next = draft.stages.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i }));
    setDraft({ ...draft, stages: next });
  };

  const addStage = () => {
    const next = [...draft.stages, {
      name: "Nuevo stage",
      stage_key: `stage_${draft.stages.length + 1}`,
      description: "",
      agent_id: null,
      order: draft.stages.length,
      entry_actions: [],
      exit_conditions: [],
    }];
    setDraft({ ...draft, stages: next });
  };

  // ---- Transiciones automáticas ----
  const transitions = draft.transitions || [];
  const stageOpts = (draft.stages || []).filter((s) => s.stage_key);

  const addTransition = () => {
    const first = stageOpts[0]?.stage_key || "";
    const second = stageOpts[1]?.stage_key || first;
    setDraft({
      ...draft,
      transitions: [
        ...transitions,
        { from_stage: first, to_stage: second, condition: { signal: "engagement_score", op: "gte", value: 0.5 } },
      ],
    });
  };

  const updateTransition = (idx, patch) => {
    const next = transitions.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    setDraft({ ...draft, transitions: next });
  };

  const updateCond = (idx, patch) => {
    const next = transitions.map((t, i) => (i === idx ? { ...t, condition: { ...(t.condition || {}), ...patch } } : t));
    setDraft({ ...draft, transitions: next });
  };

  const removeTransition = (idx) => {
    setDraft({ ...draft, transitions: transitions.filter((_, i) => i !== idx) });
  };

  const onSignalChange = (idx, signal) => {
    const meta = SIGNALS.find((s) => s.value === signal);
    let cond = { signal };
    if (meta.type === "number") cond = { signal, op: "gte", value: 0.5 };
    else if (meta.type === "bool") cond = { signal, op: "is_true", value: true };
    else if (meta.type === "enum") cond = { signal, op: "eq", value: meta.options[0] };
    else if (meta.type === "semantic") cond = { signal, op: "semantic", instruction: "" };
    updateTransition(idx, { condition: cond });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/pipelines/${pipelineId}`, {
        name: draft.name,
        description: draft.description,
        target_role: draft.target_role || null,
        target_user_side: draft.target_user_side || null,
        active: draft.active,
        stages: draft.stages,
        transitions: draft.transitions,
      });
      toast.success("Pipeline guardado");
      setDraft(null); // force re-init from fresh data
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const discard = () => { setDraft(null); reload(); };

  const remove = async () => {
    if (!confirm(`¿Borrar el pipeline "${p.name}"?\n\nEsto elimina también todas las memberships (${data.members?.length || 0} usuarios saldrán del pipeline). No borra los usuarios.`)) return;
    try {
      await api.delete(`/pipelines/${pipelineId}`);
      toast.success("Pipeline eliminado");
      navigate("/pipelines");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const isDirty = JSON.stringify({
    name: p.name, description: p.description || "", target_role: p.target_role || "",
    target_user_side: p.target_user_side || "", active: p.active,
    stages: (p.stages || []).map((s) => ({ ...s })),
    transitions: (p.transitions || []).map((t) => ({ ...t, condition: { ...(t.condition || {}) } })),
  }) !== JSON.stringify({
    name: draft.name, description: draft.description, target_role: draft.target_role,
    target_user_side: draft.target_user_side, active: draft.active,
    stages: draft.stages, transitions: draft.transitions,
  });

  return (
    <>
      <PageHeader label="Editor de pipeline" title={draft.name} description={p.slug}>
        <Link to="/pipelines" className="text-sm text-carbon-700 hover:bg-carbon-100 px-3 py-2 rounded flex items-center gap-2">
          <ArrowLeft size={14} /> Volver
        </Link>
        {isDirty && (
          <button onClick={discard} data-testid="pipeline-discard-btn" className="text-sm text-carbon-700 hover:bg-carbon-100 px-3 py-2 rounded flex items-center gap-2">
            <ArrowsClockwise size={14} /> Descartar
          </button>
        )}
        <button onClick={save} disabled={saving || !isDirty} data-testid="pipeline-save-btn" className="bg-terracotta text-white text-sm px-4 py-2 rounded flex items-center gap-2 hover:bg-terracotta-hover disabled:opacity-50">
          <FloppyDisk size={14} /> {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </PageHeader>
      <PageBody>
        {/* Metadata editor */}
        <Card className="p-5 mb-4">
          <div className="section-label mb-3">Configuración del pipeline</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-carbon-500">Nombre</span>
              <input data-testid="pipeline-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-carbon-500">Target role</span>
              <input value={draft.target_role} onChange={(e) => setDraft({ ...draft, target_role: e.target.value })}
                placeholder="founder, ambassador, etc."
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-carbon-500">Lado del usuario</span>
              <select value={draft.target_user_side} onChange={(e) => setDraft({ ...draft, target_user_side: e.target.value })}
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="">— sin definir —</option>
                <option value="migrant_side">Migrant-side (USA)</option>
                <option value="origin_side">Origin-side (país origen)</option>
                <option value="both">Ambos</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-carbon-500">Estado</span>
              <div className="mt-1 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                    data-testid="pipeline-active-toggle" />
                  {draft.active ? "Activo" : "Inactivo"}
                </label>
                <button onClick={remove} data-testid="pipeline-delete-btn"
                  className="ml-auto text-xs text-signal hover:underline flex items-center gap-1">
                  <Trash size={12} /> Borrar pipeline
                </button>
              </div>
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs text-carbon-500">Descripción</span>
              <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                rows={2} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
            </label>
          </div>
        </Card>

        {/* Stages editor */}
        <Card className="p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="section-label">Stages — {draft.stages.length}</div>
            <button onClick={addStage} data-testid="add-stage-btn"
              className="text-xs bg-terracotta text-white px-3 py-1.5 rounded flex items-center gap-1 hover:bg-terracotta-hover">
              <Plus size={12} /> Agregar stage
            </button>
          </div>
          <p className="text-xs text-carbon-500 mb-4">
            Cada stage representa un momento del flujo. El <strong>agente IA</strong> asignado es quien conversa con el usuario cuando está en ese stage.
            Si dejas un stage sin agente, la IA no responderá hasta que el usuario avance al siguiente.
          </p>
          <div className="space-y-3">
            {draft.stages.length === 0 && (
              <div className="text-sm text-carbon-400 text-center py-8 border-2 border-dashed border-carbon-200 rounded">
                Sin stages. Agrega el primero para comenzar.
              </div>
            )}
            {draft.stages.map((s, idx) => {
              const memberCount = (byStage[s.id] || []).length;
              return (
                <div key={s.id || idx} className="border border-carbon-200 rounded-md p-4 bg-page" data-testid={`stage-row-${idx}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-mono text-xs text-carbon-400 w-12">#{idx + 1}</span>
                    <input value={s.name} onChange={(e) => updateStage(idx, { name: e.target.value })}
                      placeholder="Nombre del stage"
                      className="flex-1 bg-surface border border-carbon-200 rounded px-3 py-1.5 text-sm font-medium" />
                    <span className="text-[10px] text-carbon-400">{memberCount} miembros</span>
                    <button onClick={() => moveStage(idx, -1)} disabled={idx === 0}
                      className="p-1.5 text-carbon-500 hover:text-terracotta disabled:opacity-30" title="Subir">
                      <ArrowUp size={14} />
                    </button>
                    <button onClick={() => moveStage(idx, +1)} disabled={idx === draft.stages.length - 1}
                      className="p-1.5 text-carbon-500 hover:text-terracotta disabled:opacity-30" title="Bajar">
                      <ArrowDown size={14} />
                    </button>
                    <button onClick={() => removeStage(idx)} className="p-1.5 text-carbon-500 hover:text-signal" title="Eliminar stage">
                      <Trash size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="block">
                      <span className="text-[10px] text-carbon-500 uppercase tracking-wider">Stage key (código)</span>
                      <input value={s.stage_key || ""} onChange={(e) => updateStage(idx, { stage_key: e.target.value })}
                        placeholder="ej. new_lead"
                        className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-1.5 text-xs font-mono" />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="text-[10px] text-carbon-500 uppercase tracking-wider">Agente IA del stage</span>
                      <select value={s.agent_id || ""} onChange={(e) => updateStage(idx, { agent_id: e.target.value || null })}
                        data-testid={`stage-agent-${idx}`}
                        className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-1.5 text-sm">
                        <option value="">— sin agente (no responde automáticamente) —</option>
                        {(agents || []).map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name} · {a.provider}/{a.model}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block md:col-span-3">
                      <span className="text-[10px] text-carbon-500 uppercase tracking-wider">Descripción</span>
                      <textarea value={s.description || ""} onChange={(e) => updateStage(idx, { description: e.target.value })}
                        rows={2} placeholder="¿Qué pasa en este stage?"
                        className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-1.5 text-xs" />
                    </label>
                  </div>
                  {s.agent_id && (
                    <div className="mt-2 text-[10px] text-carbon-500">
                      Agente actual: <strong className="text-carbon-700">{agentName(s.agent_id)}</strong>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Transiciones automáticas */}
        <Card className="p-5 mb-4" data-testid="transitions-card">
          <div className="flex items-center justify-between mb-3">
            <div className="section-label">Transiciones automáticas — {transitions.length}</div>
            <button onClick={addTransition} disabled={stageOpts.length < 1} data-testid="add-transition-btn"
              className="text-xs bg-terracotta text-white px-3 py-1.5 rounded flex items-center gap-1 hover:bg-terracotta-hover disabled:opacity-40">
              <Plus size={12} /> Agregar regla
            </button>
          </div>
          <p className="text-xs text-carbon-500 mb-4">
            Define <strong>cuándo la IA mueve a un lead de una etapa a otra</strong>. En cada mensaje que responde el usuario,
            la IA detecta señales (interés, engagement, si quiere unirse, etc.) y si se cumple una regla, avanza al lead automáticamente.
            {stageOpts.length < 1 && <span className="text-signal"> Primero crea etapas con un “stage key” para poder definir reglas.</span>}
          </p>

          <div className="space-y-3">
            {transitions.length === 0 && stageOpts.length >= 1 && (
              <div className="text-sm text-carbon-400 text-center py-6 border-2 border-dashed border-carbon-200 rounded">
                Sin reglas. Sin transiciones, la IA conversa pero <strong>nunca</strong> mueve al lead de etapa.
              </div>
            )}
            {transitions.map((t, idx) => {
              const meta = signalMeta(t.condition?.signal);
              return (
                <div key={idx} className="border border-carbon-200 rounded-md p-4 bg-page" data-testid={`transition-row-${idx}`}>
                  <div className="flex items-start gap-2 flex-wrap text-sm">
                    <span className="text-carbon-500 pt-2">Cuando el lead esté en</span>
                    <select value={t.from_stage} onChange={(e) => updateTransition(idx, { from_stage: e.target.value })}
                      data-testid={`transition-from-${idx}`}
                      className="bg-surface border border-carbon-200 rounded px-2 py-1.5 text-sm">
                      {stageOpts.map((s) => <option key={s.stage_key} value={s.stage_key}>{s.name}</option>)}
                    </select>
                    <span className="text-carbon-500 pt-2">y</span>
                    <select value={t.condition?.signal} onChange={(e) => onSignalChange(idx, e.target.value)}
                      data-testid={`transition-signal-${idx}`}
                      className="bg-surface border border-carbon-200 rounded px-2 py-1.5 text-sm">
                      {SIGNALS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>

                    {meta.type === "number" && (
                      <>
                        <select value={t.condition?.op || "gte"} onChange={(e) => updateCond(idx, { op: e.target.value })}
                          data-testid={`transition-op-${idx}`}
                          className="bg-surface border border-carbon-200 rounded px-2 py-1.5 text-sm">
                          <option value="gte">sea ≥</option>
                          <option value="lte">sea ≤</option>
                        </select>
                        <input type="number" step="0.1" min="0" max="1"
                          value={t.condition?.value ?? 0.5}
                          onChange={(e) => updateCond(idx, { value: parseFloat(e.target.value) })}
                          data-testid={`transition-value-${idx}`}
                          className="w-20 bg-surface border border-carbon-200 rounded px-2 py-1.5 text-sm font-mono" />
                      </>
                    )}
                    {meta.type === "bool" && (
                      <span className="text-carbon-500 pt-2">sea <strong>verdadero</strong></span>
                    )}
                    {meta.type === "enum" && (
                      <>
                        <span className="text-carbon-500 pt-2">sea</span>
                        <select value={t.condition?.value} onChange={(e) => updateCond(idx, { value: e.target.value })}
                          data-testid={`transition-value-${idx}`}
                          className="bg-surface border border-carbon-200 rounded px-2 py-1.5 text-sm">
                          {meta.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </>
                    )}
                    {meta.type === "semantic" && (
                      <span className="text-carbon-500 pt-2">se cumpla (según la conversación)</span>
                    )}

                    <span className="text-carbon-500 pt-2">→ mover a</span>
                    <select value={t.to_stage} onChange={(e) => updateTransition(idx, { to_stage: e.target.value })}
                      data-testid={`transition-to-${idx}`}
                      className="bg-surface border border-carbon-200 rounded px-2 py-1.5 text-sm">
                      {stageOpts.map((s) => <option key={s.stage_key} value={s.stage_key}>{s.name}</option>)}
                    </select>

                    <button onClick={() => removeTransition(idx)} data-testid={`transition-remove-${idx}`}
                      className="ml-auto p-1.5 text-carbon-500 hover:text-signal" title="Eliminar regla">
                      <Trash size={14} />
                    </button>
                  </div>
                  {meta.type === "semantic" && (
                    <div className="mt-3">
                      <span className="text-[10px] text-carbon-500 uppercase tracking-wider">Instrucción para la IA</span>
                      <textarea value={t.condition?.instruction || ""}
                        onChange={(e) => updateCond(idx, { instruction: e.target.value })}
                        data-testid={`transition-instruction-${idx}`}
                        rows={2}
                        placeholder="ej: cuando el lead haya contestado las tres preguntas de calificación del agente"
                        className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-2 text-sm" />
                      <span className="text-[10px] text-carbon-500 mt-1 block">
                        La IA leerá la conversación y decidirá si esta condición se cumple para mover al lead.
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Members per stage (read-only snapshot) */}
        <Card className="p-5 mb-4">
          <div className="section-label mb-3">Miembros por stage</div>
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
            {(p.stages || []).map((s, idx) => (
              <div key={s.id} className="min-w-[220px] flex flex-col">
                <div className="bg-surface border-2 border-carbon-200 rounded-md p-3 mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[10px] text-carbon-400">STAGE {idx + 1}</span>
                    <span className="text-[10px] text-carbon-400">{(byStage[s.id] || []).length}</span>
                  </div>
                  <div className="font-medium text-sm">{s.name}</div>
                </div>
                <div className="space-y-2 max-h-[260px] overflow-y-auto scrollbar-thin">
                  {(byStage[s.id] || []).map((m) => (
                    <Link to={`/users/${m.user_id}`} key={m.id} className="block">
                      <div className="bg-surface border border-carbon-200 rounded p-2 text-xs hover:border-terracotta transition-colors">
                        <div className="font-medium">{m.user?.name || m.user_id.slice(0, 8)}</div>
                        <div className="flex items-center justify-between text-[10px] text-carbon-500 mt-1">
                          <span>{m.user?.public_id}</span>
                          <span className="font-mono text-terracotta">{(m.score || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            {(p.stages || []).length === 0 && (
              <div className="text-sm text-carbon-500">Sin stages todavía — agrega arriba.</div>
            )}
          </div>
        </Card>
      </PageBody>
    </>
  );
}
